import React, { useState, useEffect, useCallback, useMemo, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Accordian from "../../../../../components/Accordian";
import Spinner from "../../../../../components/Spinner";
import Checkbox from "../../../../../components/Checkbox";
import Button from "../../../../../components/Button";
import Toast from "../../../../../components/Toast";
import TableBox from "../../../../../components/TableBox";
import Modal from "../../../../../components/Modal";
import { HeadCellType, TableContextProvider } from "../../../../../interfaces/common";
import { buildHeadCells, formatAmount, formatDollarAmount, sortRowsByAccessor } from "../../../../../utils/methods";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { SortOrder, RoleCodes } from "../../../../../enums";
import { AuthContext } from "../../../../../contexts/AuthContext";
import ManagerStatementService from "../../../../../services/managerStatement.service";
import { AdjustCommissionForm, CommissionPayoutInvoice } from "../../../../../interfaces/managerStatement.interface";
import CommissionPayoutsRow from "./CommissionPayoutsRow";
import AdjustCommissionModalContent from "./AdjustCommissionModalContent";
import AdjustCommissionModalButtons from "./AdjustCommissionModalButtons";
import PropertyVisitBifurcationModal from "./PropertyVisitBifurcationModal";

interface CommissionPayoutsSectionProps {
  statementId: string;
  managerName?: string | null;
  readOnly?: boolean;
  onTotalsChange?: (commissionEarned: number) => void;
  onLinked?: () => void;
}

export interface CommissionPayoutsSectionHandle {
  save: () => Promise<void>;
  isDirty: () => boolean;
}

const HEAD_CELLS = buildHeadCells([
  VALIDATION_FIELD_MAPPING.select,
  VALIDATION_FIELD_MAPPING.invoiceNumber,
  VALIDATION_FIELD_MAPPING.invoiceMonth,
  VALIDATION_FIELD_MAPPING.propertyName,
  VALIDATION_FIELD_MAPPING.coHostCommission,
  VALIDATION_FIELD_MAPPING.baseCommission,
  VALIDATION_FIELD_MAPPING.propertyVisitCommission,
  VALIDATION_FIELD_MAPPING.commissionEarned,
  VALIDATION_FIELD_MAPPING.commissionPayoutAction,
], 'managerStatementCommissionPayoutsList');

const COMMISSION_PAYOUTS_SORT_ACCESSORS: Record<string, (item: CommissionPayoutInvoice) => string | number | null | undefined> = {
  [VALIDATION_FIELD_MAPPING.invoiceNumber]: (item) => item.invoice_no,
  [VALIDATION_FIELD_MAPPING.invoiceMonth]: (item) => item.invoice_month_sort,
  [VALIDATION_FIELD_MAPPING.propertyName]: (item) => item.property_name,
  [VALIDATION_FIELD_MAPPING.coHostCommission]: (item) => item.co_host_commission,
  [VALIDATION_FIELD_MAPPING.baseCommission]: (item) => item.base_commission,
  [VALIDATION_FIELD_MAPPING.propertyVisitCommission]: (item) => item.property_visit_commission,
  [VALIDATION_FIELD_MAPPING.commissionEarned]: (item) => item.commission_earned,
};

const EMPTY_ADJUST_FORM: AdjustCommissionForm = {
  base_commission_override: '',
  property_visit_commission_override: '',
  manager_note: '',
  admin_note: '',
  is_resolved: false,
};

const ADJUST_FIELD_NAME_MAP: Record<string, keyof AdjustCommissionForm> = {
  [VALIDATION_FIELD_MAPPING.baseCommissionOverride]: 'base_commission_override',
  [VALIDATION_FIELD_MAPPING.propertyVisitCommissionOverride]: 'property_visit_commission_override',
  [VALIDATION_FIELD_MAPPING.managerNote]: 'manager_note',
  [VALIDATION_FIELD_MAPPING.adminNote]: 'admin_note',
  [VALIDATION_FIELD_MAPPING.markAsResolved]: 'is_resolved',
};

const CommissionPayoutsSection = forwardRef<CommissionPayoutsSectionHandle, CommissionPayoutsSectionProps>(({ statementId, managerName, readOnly, onTotalsChange, onLinked }, ref) => {
  const { t } = useTranslation();
  const { isSystemAdmin, loggedInUser } = useContext(AuthContext);
  const isAdminCaller = isSystemAdmin === 'true' || loggedInUser?.role_code === RoleCodes.admin;

  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<CommissionPayoutInvoice[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [order, setOrder] = useState<SortOrder>(SortOrder.asc);
  const [orderBy, setOrderBy] = useState<string>(VALIDATION_FIELD_MAPPING.invoiceNumber);
  const [bifurcationInvoiceId, setBifurcationInvoiceId] = useState<number | null>(null);
  const [adjustModal, setAdjustModal] = useState<{ open: boolean; item: CommissionPayoutInvoice | null }>({ open: false, item: null });
  const [adjustFormData, setAdjustFormData] = useState<AdjustCommissionForm>(EMPTY_ADJUST_FORM);
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [attaching, setAttaching] = useState(false);

  // Tracks the invoice IDs last confirmed saved to (or loaded from) the server, so isDirty()
  // can tell an in-progress selection change apart from the persisted state without a second
  // network round-trip.
  const savedSelectedIdsRef = useRef<number[]>([]);

  // silent=true skips the full-table spinner - used for a background refresh (e.g. after
  // saveSelection() re-fetches to pick up a newly attached row's real data) so the table
  // doesn't blank out and reappear for what is otherwise a routine save.
  const loadPayouts = useCallback((silent = false) => {
    if (!statementId) return Promise.resolve([] as CommissionPayoutInvoice[]);

    if (!silent) setLoading(true);
    return ManagerStatementService.getCommissionPayouts(statementId)
      .then((response: any) => {
        const rows: CommissionPayoutInvoice[] = response?.invoices ?? [];
        const rowSelectedIds = rows.filter((row) => row.selected).map((row) => row.invoice_id);
        setInvoices(rows);
        setSelectedIds(rowSelectedIds);
        savedSelectedIdsRef.current = rowSelectedIds;
        if (!silent) setLoading(false);
        return rows;
      })
      .catch(() => {
        setInvoices([]);
        setSelectedIds([]);
        savedSelectedIdsRef.current = [];
        if (!silent) setLoading(false);
        return [] as CommissionPayoutInvoice[];
      });
  }, [statementId]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  const handleSelectToggle = useCallback((invoiceId: number) => {
    setSelectedIds((prev) => (prev.includes(invoiceId) ? prev.filter((id) => id !== invoiceId) : [...prev, invoiceId]));
  }, []);

  const handleRemove = useCallback((invoiceId: number) => {
    setSelectedIds((prev) => prev.filter((id) => id !== invoiceId));
  }, []);

  const handleOpenBifurcation = useCallback((invoiceId: number) => {
    setBifurcationInvoiceId(invoiceId);
  }, []);

  const handleCloseBifurcation = useCallback(() => {
    setBifurcationInvoiceId(null);
  }, []);

  const selectableInvoiceIds = useMemo(
    () => invoices.filter((row) => !row.distance_not_configured).map((row) => row.invoice_id),
    [invoices]
  );
  const isAllSelected = selectableInvoiceIds.length > 0 && selectableInvoiceIds.every((id) => selectedIds.includes(id));

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (
      isAllSelected
        ? prev.filter((id) => !selectableInvoiceIds.includes(id))
        : [...prev, ...selectableInvoiceIds.filter((id) => !prev.includes(id))]
    ));
  }, [isAllSelected, selectableInvoiceIds]);

  const saveSelection = useCallback(() => {
    return ManagerStatementService.saveCommissionPayouts(statementId, { invoice_ids: selectedIds })
      .then(() => loadPayouts(true))
      .then((rows) => {
        // A non-empty save may have just linked the first invoice on a statement whose
        // Details tab was never saved - the backend stamps details_saved_at on that first
        // link (see saveCommissionPayouts controller action), so refresh the parent
        // statement to reflect the now-unlocked Documents/Payments tabs immediately.
        if (selectedIds.length > 0) {
          onLinked?.();
        }
        return rows;
      });
  }, [statementId, selectedIds, loadPayouts, onLinked]);

  const save = useCallback(() => saveSelection().then(() => undefined), [saveSelection]);

  const isDirty = useCallback(() => {
    const saved = savedSelectedIdsRef.current;
    if (selectedIds.length !== saved.length) return true;
    const savedSet = new Set(saved);
    return selectedIds.some((id) => !savedSet.has(id));
  }, [selectedIds]);

  useImperativeHandle(ref, () => ({ save, isDirty }), [save, isDirty]);

  const selectedInvoices = useMemo(
    () => invoices.filter((row) => selectedIds.includes(row.invoice_id)),
    [invoices, selectedIds]
  );

  // Linked (already-saved) rows float to the top, keyed off the server's persisted
  // `selected` flag so a row only jumps up after its save actually completes - not while a
  // checkbox is merely toggled ahead of that save.
  const sortedInvoices = useMemo(() => {
    const sorted = sortRowsByAccessor(invoices, orderBy, order, COMMISSION_PAYOUTS_SORT_ACCESSORS);
    return [
      ...sorted.filter((row) => row.selected),
      ...sorted.filter((row) => !row.selected),
    ];
  }, [invoices, orderBy, order]);

  const totals = useMemo(() => selectedInvoices.reduce((acc, row) => ({
    coHost: acc.coHost + Number(row.co_host_commission || 0),
    base: acc.base + Number(row.base_commission || 0),
    visit: acc.visit + Number(row.property_visit_commission || 0),
    earned: acc.earned + Number(row.commission_earned || 0),
  }), { coHost: 0, base: 0, visit: 0, earned: 0 }), [selectedInvoices]);

  useEffect(() => {
    onTotalsChange?.(totals.earned);
  }, [totals.earned, onTotalsChange]);

  const footerRow = invoices.length > 0 ? (
    <tr key="footer">
      <td colSpan={4} className="fw-semibold">
        {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.footer.totalSelected', { count: selectedIds.length })}
      </td>
      <td className="fw-semibold">{formatDollarAmount(formatAmount(totals.coHost.toFixed(2)))}</td>
      <td className="fw-semibold">{formatDollarAmount(formatAmount(totals.base.toFixed(2)))}</td>
      <td className="fw-semibold">{formatDollarAmount(formatAmount(totals.visit.toFixed(2)))}</td>
      <td className="fw-semibold">{formatDollarAmount(formatAmount(totals.earned.toFixed(2)))}</td>
      <td colSpan={1}></td>
    </tr>
  ) : undefined;

  const openAdjustModal = useCallback((item: CommissionPayoutInvoice) => {
    setAdjustFormData({
      base_commission_override: item.base_commission_override !== null ? String(item.base_commission_override) : '',
      property_visit_commission_override: item.property_visit_commission_override !== null ? String(item.property_visit_commission_override) : '',
      manager_note: item.manager_note || '',
      admin_note: item.admin_note || '',
      is_resolved: item.is_resolved,
    });
    setAdjustModal({ open: true, item });
  }, []);

  const handleAdjustClick = useCallback((item: CommissionPayoutInvoice) => {
    setAttaching(true);
    saveSelection()
      .then((rows) => {
        const updated = rows.find((row) => row.invoice_id === item.invoice_id) ?? item;
        openAdjustModal(updated);
      })
      .finally(() => setAttaching(false));
  }, [saveSelection, openAdjustModal]);

  const handleAdjustClose = useCallback(() => {
    // Keep the last item in place while the dialog fades out (Modal's Fade transition
    // keeps content/action mounted for ~150ms after open becomes false) - nulling it here
    // makes AdjustCommissionModalContent bail out early while the footer buttons keep
    // rendering, flashing a blank dialog with just buttons during the close animation.
    setAdjustModal((prev) => ({ ...prev, open: false }));
    setAdjustFormData(EMPTY_ADJUST_FORM);
  }, []);

  const handleAdjustInputChange = useCallback((e: any, name: string) => {
    const field = ADJUST_FIELD_NAME_MAP[name];
    if (!field) return;

    const value = field === 'is_resolved' ? e.target.checked : e.target.value;
    setAdjustFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleResetToAuto = useCallback((field: 'base_commission_override' | 'property_visit_commission_override') => {
    setAdjustFormData((prev) => ({ ...prev, [field]: '' }));
  }, []);

  const handleAdjustSave = useCallback(() => {
    if (!adjustModal.item) return;

    setAdjustSaving(true);

    const payload = {
      base_commission_override: adjustFormData.base_commission_override === '' ? null : Number(adjustFormData.base_commission_override),
      property_visit_commission_override: adjustFormData.property_visit_commission_override === '' ? null : Number(adjustFormData.property_visit_commission_override),
      manager_note: adjustFormData.manager_note,
      ...(isAdminCaller ? { admin_note: adjustFormData.admin_note, is_resolved: adjustFormData.is_resolved } : {}),
    };

    ManagerStatementService.adjustCommissionPayout(statementId, String(adjustModal.item.invoice_id), payload)
      .then(() => {
        toast.success(
          <Toast message={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.adjustModal.saveSuccess')} />,
          { icon: false }
        );
        setAdjustSaving(false);
        handleAdjustClose();
        loadPayouts();
      })
      .catch(() => {
        setAdjustSaving(false);
      });
  }, [adjustModal.item, adjustFormData, isAdminCaller, statementId, loadPayouts, handleAdjustClose, t]);

  const tableValues: TableContextProvider = useMemo(() => ({
    rowProps: {
      selectedIds, onSelectToggle: handleSelectToggle, onRemove: handleRemove, onAdjustClick: handleAdjustClick, onOpenBifurcation: handleOpenBifurcation, readOnly, saving: attaching,
    },
    row: CommissionPayoutsRow,
    data: sortedInvoices,
    pagination: false,
    headCells: HEAD_CELLS as HeadCellType[],
    order,
    orderBy,
    page: 1,
    perPage: 9999,
    total: invoices.length,
    loading,
    noDataFoundMessage: t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.noDataFoundMessage'),
    setPage: () => {},
    setPerPage: () => {},
    setOrder,
    setOrderBy,
    footerRow,
  }), [sortedInvoices, invoices.length, loading, order, orderBy, selectedIds, handleSelectToggle, handleRemove, handleAdjustClick, handleOpenBifurcation, readOnly, attaching, footerRow, t]);

  const summary = (
    <>
      <i className="ri-money-dollar-circle-line ri-22px me-2"></i>
      <h6 className="mb-0">
        {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.title')}
      </h6>
    </>
  );

  const content = loading ? (
    <div className="d-flex justify-content-center p-4">
      <Spinner variant="primary" />
    </div>
  ) : (
    <div>
      {invoices.length === 0 ? (
        <div className="d-flex justify-content-center p-4">
          <p className="text-muted mb-0">
            {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.empty')}
          </p>
        </div>
      ) : (
        <div className="row g-6">
          <div className="col-md-12 d-flex align-items-center justify-content-between">
            <div className="row g-3 col-md-2 align-items-center ps-5">
              <div className="form-floating form-floating-outline">
                <Checkbox
                  variant="primary"
                  className="d-checkbox mb-0"
                  name="selectAllCommissionPayouts"
                  label={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.selectAll')}
                  checked={isAllSelected}
                  disabled={readOnly}
                  onChange={handleSelectAll}
                />
              </div>
            </div>
            <div className="row pe-5">
              <div className="d-flex col-md-2 align-items-center ps-5 gap-3">
                <div className="d-flex align-items-center text-nowrap">
                  <i className="ri-circle-fill ri-16px me-1 text-warning"></i>
                  <span>{t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.legend.needsReview')}</span>
                </div>
                <div className="d-flex align-items-center text-nowrap">
                  <i className="ri-circle-fill ri-16px me-1 text-primary"></i>
                  <span>{t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.legend.manuallyAdjusted')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <TableBox tableValues={tableValues} />
          </div>
        </div>
      )}
    </div>
  );

  const adjustSubtitle = adjustModal.item
    ? [adjustModal.item.invoice_no, [adjustModal.item.property_name, managerName].filter(Boolean).join(' - ')].filter(Boolean).join(' · ')
    : '';

  const adjustModalTitle = (
    <div className="d-flex justify-content-between align-items-start w-100">
      <div>
        <h5 className="fw-bold mb-0">
          {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.adjustModal.title')}
        </h5>
        {adjustSubtitle && <div className="text-body small fw-semibold">{adjustSubtitle}</div>}
      </div>
      <Button
        className="btn btn-sm btn-icon btn-text-secondary"
        icon={<i className="ri-close-line ri-20px" />}
        onClick={handleAdjustClose}
      />
    </div>
  );

  return (
    <>
      <Accordian
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        summary={summary}
        content={content}
        bodyClassName="pb-0 px-0"
      />
      <PropertyVisitBifurcationModal
        open={bifurcationInvoiceId !== null}
        statementId={statementId}
        invoiceId={bifurcationInvoiceId}
        onClose={handleCloseBifurcation}
      />
      <Modal
        open={adjustModal.open}
        onClose={handleAdjustClose}
        maxWidth="sm"
        title={adjustModalTitle}
        titleClassName="text-start"
        actionClassName="justify-content-start"
        content={(
          <AdjustCommissionModalContent
            item={adjustModal.item}
            formData={adjustFormData}
            isAdminCaller={isAdminCaller}
            readOnly={readOnly}
            onInputChange={handleAdjustInputChange}
            onResetToAuto={handleResetToAuto}
          />
        )}
        action={(
          <AdjustCommissionModalButtons
            loading={adjustSaving}
            readOnly={readOnly}
            onCancel={handleAdjustClose}
            onSave={handleAdjustSave}
          />
        )}
      />
    </>
  );
});

CommissionPayoutsSection.displayName = 'CommissionPayoutsSection';

export default CommissionPayoutsSection;
